import { SjaHazardRisk } from "../../../../../components/dashboard/hse/risk/SjaHazardFields";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { CheckIcon, PencilIcon, PaperAirplaneIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { HsePageChrome, useHseBusyAction } from "../../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { useUserHasAccess } from "../../../../../routes/roleOnly";
import { approveSja, exportSjaPdf, fetchSja, submitSja } from "../../../../../api/hse/risk";
import { HsePageBody } from "../../../../../components/dashboard/hse/HsePageBody";
import { RiskLevelBadge } from "../../../../../components/dashboard/hse/risk/RiskLevelBadge";
import { formatDisplayDate, riskApiError } from "../../../../../components/dashboard/hse/risk/riskUtils";
import { sjaWorkflowActions } from "../../../../../components/dashboard/hse/risk/sjaApprovalUtils";

function roleLabel(t, role) {
  return t(`hse.sja.role.${role}`, { defaultValue: role });
}

export const HseKsSjaView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const canEdit = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { run, busyProps } = useHseBusyAction();

  const workflow = useMemo(
    () => sjaWorkflowActions({ record, canEdit }),
    [record, canEdit]
  );

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      setRecord(await fetchSja(id));
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.sja.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = () =>
    run("submit", async () => {
      try {
        setRecord(await submitSja(id));
        toast.success(t("hse.sja.submitted"));
      } catch (err) {
        toast.error(riskApiError(err) || t("hse.risk.save_failed"));
      }
    });

  const handleApprove = (role) =>
    run(`approve-${role || "generic"}`, async () => {
      try {
        setRecord(await approveSja(id, { role }));
        toast.success(t("hse.sja.approved"));
      } catch (err) {
        toast.error(riskApiError(err) || t("hse.risk.save_failed"));
      }
    });

  const approval = record?.approval || {};

  return (
    <HsePageChrome
      title={record?.title || t("hse.risk.sja.view_title")}
      subtitle={t("hse.risk.sja.view_subtitle")}
      backTo="/dashboard/hse-ks/risk/sja"
      mainButton={{
        label: t("hse.risk.edit"),
        icon: PencilIcon,
        path: `/dashboard/hse-ks/risk/sja/${id}/edit`,
        hidden: !workflow.canEditRecord || loading || !!error,
      }}
      actions={[
        {
          label: t("hse.risk.pdf"),
          icon: PrinterIcon,
          action: () =>
            run("pdf", async () => {
              try {
                await exportSjaPdf(id, `hse-sja-${id}.pdf`);
                toast.success(t("hse.risk.pdf_ready"));
              } catch (err) {
                toast.error(err.message || t("hse.risk.pdf_failed"));
              }
            }),
          hidden: loading || !!error || !record,
          menu: false,
          ...busyProps("pdf"),
        },
        {
          label: t("hse.sja.submit"),
          icon: PaperAirplaneIcon,
          action: handleSubmit,
          hidden: !workflow.canSubmit || loading || !!error,
          menu: false,
          ...busyProps("submit"),
        },
        ...workflow.approveRoles.map((role) => ({
          label: t(`hse.sja.approve_${role}`),
          icon: CheckIcon,
          action: () => handleApprove(role),
          hidden: loading || !!error,
          menu: false,
          ...busyProps(`approve-${role}`),
        })),
        {
          label: t("hse.sja.approve"),
          icon: CheckIcon,
          action: () => handleApprove(),
          hidden: !workflow.canApproveGeneric || loading || !!error,
          menu: false,
          ...busyProps("approve-generic"),
        },
      ]}
    >
      <HsePageBody narrow>
        {loading ? (
          <ResourceLoading />
        ) : error ? (
          <ResourceError
            variant={!record ? "not_found" : "load_error"}
            errors={[error]}
            onRetry={load}
            backTo="/dashboard/hse-ks/risk/sja"
          />
        ) : (
          <div className="space-y-6">
            <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">
                  {t(`hse.risk.sja.status.${record.status}`)}
                </span>
                {record.overall_risk_level ? (
                  <RiskLevelBadge level={record.overall_risk_level} />
                ) : null}
              </div>
              <p className="mt-2 text-xs text-slate-500">{t("hse.sja.status_follows_approval")}</p>
              {record.not_editable ? (
                <p className="mt-2 text-xs text-slate-500">{t("hse.sja.not_editable")}</p>
              ) : null}
              <p className="mt-3 text-sm text-slate-600">
                {t(`hse.risk.sja.work_type.${record.work_type}`)}
                {record.work_location ? ` · ${record.work_location}` : ""}
              </p>
              {record.description ? (
                <p className="mt-3 whitespace-pre-wrap text-sm text-slate-800">{record.description}</p>
              ) : null}
              <p className="mt-4 text-xs text-slate-500">
                {t("hse.risk.view.updated", { date: formatDisplayDate(record.updated_at) })}
              </p>
            </div>

            <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-extrabold text-slate-900">{t("hse.sja.approval.title")}</h3>
              {approval.pending_roles?.length ? (
                <p className="mt-2 text-sm text-slate-600">
                  {t("hse.sja.approval.pending")}:{" "}
                  {approval.pending_roles.map((role) => roleLabel(t, role)).join(", ")}
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-600">{t("hse.sja.approval.empty")}</p>
              )}
              {approval.recorded?.length ? (
                <ul className="mt-3 space-y-2">
                  {approval.recorded.map((item, index) => (
                    <li key={`${item.role}-${item.user_id || index}`} className="text-sm text-slate-700">
                      {roleLabel(t, item.role)}
                      {item.user?.name ? ` · ${item.user.name}` : ""}
                      {item.approved_at
                        ? ` · ${formatDisplayDate(item.approved_at)}`
                        : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-extrabold text-slate-900">{t("hse.risk.sja.steps")}</h3>
              <ol className="mt-4 space-y-4">
                {(record.steps || []).map((step, index) => (
                  <li key={step.id || index} className="rounded-[14px] bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase text-slate-500">
                      {t("hse.risk.sja.step_n", { n: step.step_number || index + 1 })}
                    </p>
                    <p className="mt-1 text-sm text-slate-800">{step.description || "—"}</p>
                    {step.hazards.map((hazard, hazardIndex) => (
                      <div key={hazard.id || hazardIndex} className="mt-3 space-y-2 border-t border-slate-200 pt-3 text-sm">
                        <p className="whitespace-pre-wrap">{hazard.description}</p>
                        <SjaHazardRisk hazard={hazard} />
                        {["existing_measures", "additional_measures"].map((field) => hazard[field] && (
                          <p key={field} className="whitespace-pre-wrap"><strong>{t(`hse.sja.fields.${field}`)}: </strong>{hazard[field]}</p>
                        ))}
                      </div>
                    ))}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </HsePageBody>
    </HsePageChrome>
  );
};
